import { useState, useRef, useEffect, forwardRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../utils/utils';

import { useOutsideClick } from '../../hooks/useOutsideClick';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onDebouncedChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  history?: string[];
  showHistory?: boolean;
  /**
   * Render with the recent-search dropdown already open.
   *
   * The dropdown is otherwise gated on focus, which is internal state with no
   * prop — so the list could never be a resting frame for a visual diff.
   * Initial state only; no production call site passes it.
   */
  defaultOpen?: boolean;
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  clearable?: boolean;
  debounceMs?: number;
}

const sizeClasses = {
  sm: 'h-8 text-sm',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
};

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onDebouncedChange,
      onSubmit,
      placeholder = 'Search...',
      history = [],
      showHistory = true,
      isLoading = false,
      size = 'md',
      className,
      clearable = true,
      debounceMs = 300,
      defaultOpen = false,
    },
    ref
  ) => {
    const [showDropdown, setShowDropdown] = useState(defaultOpen);
    const [, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wasInitiallyFocused = useRef(false);

    // Restore focus after re-renders if it was previously focused
    useEffect(() => {
      if (
        wasInitiallyFocused.current &&
        inputRef.current &&
        document.activeElement !== inputRef.current
      ) {
        inputRef.current.focus();
      }
    });

    const dropdownRef = useOutsideClick<HTMLDivElement>(() => {
      setShowDropdown(false);
    }, showDropdown);

    // Internal debouncing for onDebouncedChange
    useEffect(() => {
      if (!onDebouncedChange) return;

      const timer = setTimeout(() => {
        onDebouncedChange(value);
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [value, onDebouncedChange, debounceMs]);

    const handleSubmit = (searchValue: string) => {
      if (searchValue.trim()) {
        onSubmit?.(searchValue.trim());
        setShowDropdown(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(value);
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      wasInitiallyFocused.current = true;
      if (showHistory && history.length > 0 && !value) {
        setShowDropdown(true);
      }
    };

    const handleBlur = (e: React.FocusEvent) => {
      // Only lose focus if clicking outside the entire search container
      if (!containerRef.current?.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
        wasInitiallyFocused.current = false;
      }
    };

    const handleInputChange = (newValue: string) => {
      onChange(newValue);
      if (showHistory && history.length > 0) {
        setShowDropdown(newValue.length === 0);
      }
    };

    const handleHistorySelect = (historyItem: string) => {
      onChange(historyItem);
      handleSubmit(historyItem);
    };

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        <div
          className={cn(
            'relative flex items-center',
            'bg-card',
            // Transparent 1px border by default so flipping to indigo on
            // focus-within doesn't cause a layout shift. The inner
            // <input> is intentionally borderless - the wrapper owns the
            // visible focus state.
            'rounded-lg border border-transparent transition-all duration-200',
            'dark:focus-within:border-primary dark:focus-within:border-primary focus-within:border-indigo-400',
            sizeClasses[size]
          )}
        >
          <Search
            className={cn(
              'pointer-events-none absolute left-3',
              'h-4 w-4',
              'text-muted-foreground'
            )}
          />

          <input
            ref={node => {
              inputRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref && 'current' in ref) {
                (
                  ref as React.MutableRefObject<HTMLInputElement | null>
                ).current = node;
              }
            }}
            type='text'
            value={value}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={cn(
              'w-full border-none bg-transparent pr-10 pl-10 outline-none',
              'focus:border-none focus:ring-0 focus:outline-none',
              'placeholder:text-muted-foreground dark:placeholder:text-muted-foreground',
              'text-foreground'
            )}
            style={{ outline: 'none', boxShadow: 'none' }}
            disabled={isLoading}
          />

          {clearable && value && (
            <button
              onClick={() => onChange('')}
              className={cn(
                'absolute right-3 rounded-full p-0.5 transition-colors',
                'hover:bg-muted'
              )}
            >
              <X className='h-3 w-3' />
            </button>
          )}

          {isLoading && (
            <div className='absolute right-3'>
              <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600' />
            </div>
          )}
        </div>

        {/* Search History Dropdown */}
        {showDropdown && showHistory && history.length > 0 && (
          <div
            ref={dropdownRef}
            className={cn(
              'absolute top-full right-0 left-0 z-50 mt-1',
              'max-h-64 overflow-y-auto',
              'bg-card',
              'border-border',
              'rounded-lg border shadow-lg'
            )}
          >
            <div className='p-2'>
              <div
                className={cn(
                  'mb-2 text-xs font-medium',
                  'text-muted-foreground'
                )}
              >
                Recent Searches
              </div>
              {history.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistorySelect(item)}
                  className={cn(
                    'w-full rounded-md px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-muted',
                    'text-foreground'
                  )}
                >
                  <Search className='mr-2 inline h-3 w-3' />
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
